import Vue from 'vue'
import Vuex from 'vuex'
import robots from "./js/robot"
import * as authApi from '@/utils/auth'
import IndexedDBService from '@/utils/db';
import { nanoid } from 'nanoid';

Vue.use(Vuex)

export default new Vuex.Store({
    state: {
        token: authApi.getToken(),
        user: authApi.getUser(),
        id: 0,
        sessionId: '',
        robotId: '',
        messageStorage: {},
        robotSessionMap: {},
        papers: [],
        robots: robots,
        timer: null,
        foldHistory: false,
        foldRobot: false,
        theme: 'light',
        isMobile: false,
        deviceHeight: 0,
        isKeyboardOpen: false,
        lastLevelType: 'h',
        level: '',
        conversations: [],
        activeConversation: null,
        dbInitialized: false
    },
    mutations: {
        setToken(state, token) {
            state.token = token
            authApi.setToken(token)
            authApi.removeZsToken()
        },
        removeToken(state) {
            state.token = ""
            authApi.removeToken()
            authApi.removeZsToken()
        },
        setUser(state, user) {
            Object.keys(user).forEach(key => {
              if (state.user[key] !== user[key]) {
                state.user[key] = user[key];
              }
            });
            authApi.setUser(state.user);
        },
        removeUser(state) {
            state.user = {}
            authApi.removeUser()
        },
        setSessionId(state, sessionId) {
            state.sessionId = sessionId
        },
        setRobotId(state, robotId) {
            state.robotId = robotId
        },
        removeSession(state, sessionId) {
            Vue.delete(state.activeConversation, sessionId)
        },
        removeSessions(state, robotId) {
            state.conversations = state.conversations.filter((conv) => conv.aiType !== robotId)
        },
        setPapers(state, papers) {
            state.papers = papers
        },
        addPaper(state, paper) {
            state.papers.push(paper)
        },
        removePaper(state, paperId) {
            let idx = state.papers.findIndex(paper => paper.id === paperId)
            if (idx !== -1) {
                state.papers.splice(idx, 1)
            }
        },
        SET_DEVICE_TYPE(state, isMobile) {
            state.isMobile = isMobile;
        },
        SET_DEVICE_HEIGHT(state, height) {
            state.deviceHeight = height;
        },
        SET_isKeyboardOpen(state, flag) {
            state.isKeyboardOpen = flag
        },
        SET_lastLevelType(state, flag) {
            state.lastLevelType = flag
        },
        setLevel(state, grade) {
            if (!grade) {
                state.level = 'h'
                return
            }
            const isPrimary = [
                '一年级', '二年级', '三年级', '四年级', '五年级', '六年级',
                '小学一年级', '小学二年级', '小学三年级', '小学四年级', '小学五年级', '小学六年级'
            ].some(g => grade.startsWith(g));
            if (isPrimary) {
                state.level = 'p'
                return
            }
            const isMiddle = ['七年级', '八年级', '九年级', '初一', '初二', '初三'].some(g => grade.startsWith(g));
            if ('七年级八年级九年级初一初二初三'.includes(grade) || isMiddle) {
                state.level = 'm'
            } else {
                state.level = 'h'
            }
        },
        ADD_ID(state) {
            state.id++
        },
        ADD_NEWCHAT(state, { robotId, sessionId }) {
            Vue.set(state.messageStorage[robotId], sessionId, {
                cname: '新会话',
                chatList: [],
                isInput: false
            })
        },
        SET_MESSAGESTORAGE(state) {
            for (const key in state.robots) {
                Vue.set(state.messageStorage, key, {})
            }
        },
        INIT_ROBOTSESSIONMAP(state) {
            for (const key in state.robots) {
                Vue.set(state.robotSessionMap, key, state.sessionId)
            }
        },
        SET_ROBOTSESSIONMAP(state, { robotId, sessionId }) {
            state.robotSessionMap[robotId] = sessionId
        },
        SET_FOLDHISTORY(state, status = true) {
            state.foldHistory = status
        },
        SET_FOLDROBOT(state, status = true) {
            state.foldRobot = status
        },
        UPDATE_MESSAGE(state, { robotId, key, msg, type = null }) {
            try {
                if (!msg) return;
                if (!state.messageStorage[robotId]) {
                    Vue.set(state.messageStorage, robotId, {})
                }
                let chat = state.messageStorage[robotId][key]
                if (!chat) {
                    chat = {
                        cname: '新会话',
                        chatList: [],
                        isInput: false,
                    }
                    Vue.set(state.messageStorage[robotId], key, chat);
                }
                let current = chat.chatList.push(msg)
                if ((robotId === 'exercises' || robotId === 'mexercises' || robotId === 'pexercises') && current === 3) {
                    if (type === 'wall') {
                        chat.cname = '##你的知识墙'
                        return
                    }
                    chat.cname = msg.msg || (msg.fileList && msg.fileList[0]?.name) || (msg.parts && msg.parts[0]?.title)
                    if (!chat.cname) chat.cname = '新会话'
                }
                if (robotId === 'composition' && current === 2) {
                    chat.cname = msg.title || msg.msg || (msg.fileList && msg.fileList[0]?.name)
                    if (!chat.cname) chat.cname = '新会话'
                }
            } catch (error) {
                console.log("===========error==============", error)
            }
        },
        UPDATE_NAME(state, { key, name }) {
            Vue.set(state.messageStorage[state.robotId][key], "cname", name)
        },

        setConversations(state, conversations) {
            state.conversations = conversations;
        },
        addConversation(state, conversation) {
            state.conversations.push(conversation);
        },
        // 原有的异步 mutation，保持不动，供其他地方使用
        async updateConversation(state, updatedConversation) {
            const index = state.conversations.findIndex(
                conv => conv.id === updatedConversation.id
            );
            if (index !== -1) {
                state.conversations.splice(index, 1, updatedConversation);
            }
            await IndexedDBService.saveConversation(updatedConversation);
        },
        deleteConversation(state, ids) {
            const idsToDelete = Array.isArray(ids) ? ids : [ids];
            state.conversations = state.conversations.filter(conv => idsToDelete.includes(conv.id) === false);
            if (state.activeConversation && idsToDelete.includes(state.activeConversation.id)) {
                state.activeConversation = null;
            }
        },
        setActiveConversation(state, conversation) {
            state.activeConversation = conversation;
        },
        clearAllConversations(state) {
            state.conversations = [];
            state.activeConversation = null;
        },
        setDbInitialized(state, status) {
            state.dbInitialized = status;
        },

        // 新增：纯同步的状态替换，不操作数据库，避免重复写入
        SYNC_UPDATE_CONVERSATION(state, updatedConversation) {
            const index = state.conversations.findIndex(
                conv => conv.id === updatedConversation.id
            );
            if (index !== -1) {
                state.conversations.splice(index, 1, updatedConversation);
            }
        },

        CLEAR_MESSAGE(state) {
            state.messageStorage = {}
            state.papers = {}
            authApi.removeZsToken()
        },
        setTheme(state, theme) {
            state.theme = theme
            localStorage.setItem("air-conditioner-theme", theme)
            document.head
                .querySelector("#theme-link")?.setAttribute("href", `./styles/theme-${theme}.css`)
        },
        getTheme(state) {
            state.theme = localStorage.getItem("air-conditioner-theme") || "light"
            document.head
                .querySelector("#theme-link")?.setAttribute("href", `./styles/theme-${state.theme}.css`)
        }
    },
    actions: {
        async initDB({ commit, dispatch }) {
            try {
                await IndexedDBService.init();
                commit('setDbInitialized', true);
                await dispatch('loadAllConversations');
                return true;
            } catch (error) {
                console.error('数据库初始化失败:', error);
                return false;
            }
        },
        async loadAllConversations({ commit }) {
            try {
                const conversations = await IndexedDBService.getAllConversations();
                // ★ 数据迁移：给所有没有 id 的消息补上 nanoid
                conversations.forEach(conv => {
                    if (conv.messages && conv.messages.length > 0) {
                        conv.messages.forEach(msg => {
                            if (!msg.id) {
                                msg.id = nanoid();
                            }
                        });
                    }
                });
                commit('setConversations', conversations);
                return conversations;
            } catch (error) {
                console.error('加载所有对话失败:', error);
                return [];
            }
        },
        async loadConversationsByAiType(_, aiType) {
            try {
                return await IndexedDBService.getConversationsByAiType(aiType);
            } catch (error) {
                console.error(`加载${aiType}类型对话失败:`, error);
                return [];
            }
        },
        async createConversation({ commit }, { aiType, id, name }) {
            try {
                const newConversation = {
                    aiType,
                    id,
                    messages: [],
                    name,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                };
                // 深拷贝后保存，确保可结构化克隆
                await IndexedDBService.saveConversation(JSON.parse(JSON.stringify(newConversation)));
                commit('addConversation', newConversation);
                commit('setActiveConversation', newConversation);
                commit('SET_ROBOTSESSIONMAP', { robotId: aiType, sessionId: id });
                return newConversation;
            } catch (error) {
                console.error('创建新对话失败:', error);
                return null;
            }
        },

        // 优化后的添加消息，消除重复数据库写入，移除不必要深拷贝
        async addMessageToConversation({ commit, state }, { message, type }) {
            try {
                // 1. 从 IndexedDB 获取对话
                const conversation = await IndexedDBService.getConversationById(message.sessionId);
                if (!conversation) {
                    throw new Error(`ID为${message.sessionId}的对话不存在`);
                }

                // 2. 查找并准备当前对话对象（state.conversations 中）
                let currIdx = 0;
                let currConv = state.conversations.find((conv, index) => {
                    if (conv.id === message.sessionId) {
                        currIdx = index;
                        return true;
                    }
                    return false;
                });

                // 3. 初始化消息数组（如果未定义）
                if (!conversation.messages) {
                    conversation.messages = [];
                    conversation.name = "新会话";
                }
                if (!currConv) {
                    // 如果 state 中不存在该对话（理论上不应发生），创建一个临时对象
                    currConv = {
                        id: message.sessionId,
                        messages: [],
                        name: "新会话",
                        aiType: message.robotId,
                    };
                    state.conversations.push(currConv);
                    currIdx = state.conversations.length - 1;
                }
                if (!currConv.messages) {
                    currConv.messages = [];
                }

                // 4. 添加时间戳
                message.timestamp = new Date().toISOString();

                // 5. 推入消息（同时更新 IndexedDB 对象和 state 对象）
                conversation.messages.push(message);
                let current = currConv.messages.push(message); // current 为消息数量（1-based）

                // 6. 更新对话名称（原业务逻辑）
                if (conversation.name === '新会话') {
                    let robotId = message.robotId;
                    if ((robotId === 'exercises' || robotId === 'mexercises' || robotId === 'pexercises') && current === 3) {
                        if (type === 'wall') {
                            conversation.name = '你的知识墙';
                        } else {
                            let name = message.msg || (message.fileList && message.fileList[0]?.name) || (message.parts && message.parts[0]?.title);
                            if (name) conversation.name = name;
                        }
                    }
                    if (robotId === 'composition' && current === 2) {
                        let name = message.title || message.msg || (message.fileList && message.fileList[0]?.name);
                        if (name) conversation.name = name;
                    }
                }

                // 7. 同步 name 到 state 对象
                currConv.name = conversation.name;

                // 8. ★★★ 关键修复：如果当前活跃对话正是该会话，更新 activeConversation 引用 ★★★
                if (state.activeConversation && state.activeConversation.id === message.sessionId) {
                    // 使用 mutation 更新 activeConversation，确保响应式
                    commit('setActiveConversation', currConv);
                }

                // 9. 更新 state.conversations 数组元素（确保响应式）
                Vue.set(state.conversations, currIdx, currConv);

                // 10. 同步更新到 IndexedDB（深拷贝以避免存储不可克隆对象）
                conversation.updatedAt = new Date().toISOString();
                await IndexedDBService.saveConversation(JSON.parse(JSON.stringify(conversation)));

                // 11. 可选：同步到 state（如果希望保持其他地方的引用一致）
                commit('SYNC_UPDATE_CONVERSATION', currConv);

                return currConv;
            } catch (error) {
                console.error('添加消息到对话失败:', error, message.sessionId);
                return null;
            }
        },

        // 优化后的流式更新，移除不必要深拷贝
        async updateMessageToConversation({ commit, state }, { message, type }) {
            try {
                const conversation = await IndexedDBService.getConversationById(message.sessionId);
                if (!conversation) {
                    throw new Error(`ID为${message.sessionId}的对话不存在`);
                }

                if (!conversation.messages) {
                    conversation.messages = [];
                }

                // 从后向前查找并替换（不再手动深拷贝）
                for (let i = conversation.messages.length - 1; i >= 0; i--) {
                    if (conversation.messages[i].id === message.id) {
                        conversation.messages.splice(i, 1, message);
                        if (i === 2 && conversation.name === 'loading') {
                            let parts = message.parts;
                            let newName = parts && parts[0] && parts[0].title;
                            if (newName) {
                                conversation.name = newName;
                            }
                        }
                        break;
                    }
                }

                conversation.updatedAt = new Date().toISOString();
                // 深拷贝后保存
                await IndexedDBService.saveConversation(JSON.parse(JSON.stringify(conversation)));

                // 同步 state（更新对应对话的名称和消息）
                let currConv = state.conversations.find(conv => conv.id === message.sessionId);
                if (currConv) {
                    const msgIndex = currConv.messages.findIndex(m => m.id === message.id);
                    if (msgIndex !== -1) {
                        currConv.messages.splice(msgIndex, 1, message);
                    }
                    if (conversation.name !== currConv.name) {
                        currConv.name = conversation.name;
                    }
                }

                return conversation;
            } catch (error) {
                console.error('更新消息到对话失败:', error, message.sessionId);
                return null;
            }
        },

        // 更新对话名称
        async updateConversationName({ commit }, { conversationId, newName }) {
            try {
                const conversation = await IndexedDBService.getConversationById(conversationId);
                if (!conversation) {
                    throw new Error(`ID为${conversationId}的对话不存在`);
                }
                conversation.name = newName;
                conversation.updatedAt = new Date().toISOString();
                // 深拷贝后保存
                await IndexedDBService.saveConversation(JSON.parse(JSON.stringify(conversation)));
                return conversation;
            } catch (error) {
                console.error('更新对话名称失败:', error);
                return null;
            }
        },
        async deleteConversation({ commit }, conversationId) {
            try {
                await IndexedDBService.deleteConversations(conversationId);
                commit('deleteConversation', conversationId);
                return true;
            } catch (error) {
                console.error('删除对话失败:', error);
                return false;
            }
        },
        async switchActiveConversation({ commit }, conversationId) {
            try {
                const conversation = await IndexedDBService.getConversationById(conversationId);
                if (conversation) {
                    // ★ 数据迁移：给该对话中缺失 id 的消息补充 ID
                    if (conversation.messages && conversation.messages.length > 0) {
                        conversation.messages.forEach(msg => {
                            if (!msg.id) {
                                msg.id = nanoid();
                            }
                        });
                    }
                    commit('setActiveConversation', conversation);
                    return conversation;
                }
                return null;
            } catch (error) {
                console.error('切换活跃对话失败:', error);
                return null;
            }
        },
        async clearAllConversations({ commit }) {
            try {
                await IndexedDBService.clearAllConversations();
                commit('clearAllConversations');
                return true;
            } catch (error) {
                console.error('清空所有对话失败:', error);
                return false;
            }
        }
    },
    getters: {
        allConversations: state => state.conversations,
        activeConversation: state => state.activeConversation,
        conversationsByAiType: (state) => (aiType) => {
            return state.conversations
              .filter(conv => conv.aiType === aiType)
              .sort((a, b) => {
                const timeA = new Date(a.updatedAt || a.createdAt).getTime();
                const timeB = new Date(b.updatedAt || b.createdAt).getTime();
                return timeB - timeA;
              });
        },
        isDbInitialized: state => state.dbInitialized
    }
})